import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Divider, ToggleButtonGroup, ToggleButton, FormControl,
  InputLabel, Select, MenuItem, TextField, Button, Alert, CircularProgress, Chip,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { listAppContexts, getComplianceRetention, setComplianceRetention } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// Compliance retention (ADR-0035 §D1 / #419) — OWNER-gated dev-portal-only control for
// DeveloperComplianceRetentionHandler's /developer/compliance-retention.
//
// Two levels, same resolution precedence the purge engine reads at purge time (context >
// tenant > platform default): the tenant-wide default (no contextId), and an optional
// per-context override. The context picker reuses `listAppContexts` — the same
// OWNER-gated, tenant-wide enumeration TriggerFailuresPage/ApiLogsPage already use — rather
// than building a second context-fetching path.
//
// The backend PATCH sets BOTH fields on every call (no partial-update-if-present semantics),
// so this is an EDITOR for the current value, not a blank form: on every successful GET the
// local form state is resynced from the fetched value (see the effect below), and Save always
// sends the form's full current state back.
// ---------------------------------------------------------------------------

const TENANT_LEVEL = '';

const DAYS_MODE_DEFAULT = 'default';
const DAYS_MODE_INDEFINITE = 'indefinite';
const DAYS_MODE_CUSTOM = 'custom';

const BASE_DISPOSITIONS = [
  { value: 'delete', label: 'Delete' },
  { value: 'retain', label: 'Retain' },
  { value: 'retain-redacted', label: 'Retain (redacted)' },
];

function daysToMode(days) {
  if (days === null || days === undefined) return DAYS_MODE_DEFAULT;
  if (days === -1) return DAYS_MODE_INDEFINITE;
  return DAYS_MODE_CUSTOM;
}

/** Inverse of daysToMode — the value PATCH should send for the current mode. */
function modeToDays(mode, customValue) {
  if (mode === DAYS_MODE_INDEFINITE) return -1;
  if (mode === DAYS_MODE_CUSTOM) {
    const n = Number(customValue);
    return Number.isInteger(n) && n > 0 ? n : null;
  }
  return null; // DAYS_MODE_DEFAULT
}

function formatDays(days) {
  if (days === -1) return 'Indefinite';
  // Only `effectiveDays` (always backend-resolved to a concrete number) reaches this function in
  // most call sites; a raw, UNRESOLVED value (tenantDefaultDays/contextOverrideDays) can be null
  // when nothing is set at that level — render that as "unset", never the literal string "null".
  if (days === null || days === undefined) return 'unset (platform default)';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatDisposition(disposition) {
  const found = BASE_DISPOSITIONS.find((o) => o.value === disposition);
  return found ? found.label : (disposition || '—');
}

// The purge engine resolves context > tenant > platform default (2557 days / retain-redacted).
// Clearing a field to null does NOT mean "platform default" — it means "fall through to the next
// level", and at the CONTEXT level that next level is the TENANT default, which may be anything
// (including `delete`). A bare "Platform default" label at the context level is misleading enough
// to cause an admin to unintentionally shorten/delete retention they meant to protect — so the
// "clear" option's label is computed from what it actually falls through to, not a static string.
function clearDaysLabel(contextId, retention) {
  if (!contextId) return 'Platform default (~2557 days / 7 years)';
  const tenantDays = retention?.tenantDefaultDays;
  return `Tenant default (currently: ${formatDays(tenantDays ?? null)})`;
}

function clearDispositionLabel(contextId, retention) {
  if (!contextId) return 'Platform default (retain-redacted)';
  const tenantDisposition = retention?.tenantDefaultDisposition;
  return `Tenant default (currently: ${formatDisposition(tenantDisposition ?? null)})`;
}

export default function ComplianceRetentionSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [env, setEnv] = useState('test');
  const [contextId, setContextId] = useState(TENANT_LEVEL);
  const [daysMode, setDaysMode] = useState(DAYS_MODE_DEFAULT);
  const [customDays, setCustomDays] = useState('');
  const [disposition, setDisposition] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Same query key shape as TriggerFailuresPage/ApiLogsPage — best-effort; a failure here just
  // leaves the picker on "Tenant default".
  const {
    data: contexts = [], isLoading: contextsLoading, isError: contextsError, error: contextsErrorObj,
  } = useQuery(['devAppContexts', env], () => listAppContexts(env), { enabled: !!user });

  const retentionQueryKey = ['compliance-retention', env, contextId || null];
  const { data: retention, isLoading: retentionLoading, error: retentionError } = useQuery(
    retentionQueryKey,
    () => getComplianceRetention(env, contextId || undefined),
    { enabled: !!user },
  );

  useEffect(() => {
    if (!retention) return;
    const days = contextId ? retention.contextOverrideDays : retention.tenantDefaultDays;
    const disp = contextId ? retention.contextOverrideDisposition : retention.tenantDefaultDisposition;
    setDaysMode(daysToMode(days));
    setCustomDays(typeof days === 'number' && days > 0 ? String(days) : '');
    setDisposition(disp || '');
    setSaveSuccess(false);
  }, [retention, contextId]);

  const { mutate: save, isLoading: saving, error: saveError } = useMutation(
    () =>
      setComplianceRetention(env, contextId || undefined, {
        complianceRetentionDays: modeToDays(daysMode, customDays),
        complianceRetentionDisposition: disposition || null,
      }),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(retentionQueryKey, data);
        setSaveSuccess(true);
      },
    },
  );

  const customDaysInvalid =
    daysMode === DAYS_MODE_CUSTOM
    && (!customDays.trim() || !Number.isInteger(Number(customDays)) || Number(customDays) < 1);

  // Guard against a stale contextId (e.g. a context deleted, or the live/test toggle changing
  // the available set) rendering a Select value with no matching MenuItem.
  const contextValue =
    contextId === TENANT_LEVEL || contexts.some((c) => c.contextId === contextId)
      ? contextId
      : TENANT_LEVEL;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={env}
          onChange={(_, v) => {
            if (!v) return;
            setEnv(v);
            setContextId(TENANT_LEVEL);
          }}
        >
          <ToggleButton value="test" sx={{ fontSize: '0.8rem', px: 2 }}>Test</ToggleButton>
          <ToggleButton value="live" sx={{ fontSize: '0.8rem', px: 2 }}>Live</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="cr-context-label">Level</InputLabel>
          <Select
            labelId="cr-context-label"
            label="Level"
            value={contextValue}
            disabled={contextsLoading}
            onChange={(e) => setContextId(e.target.value)}
          >
            <MenuItem value={TENANT_LEVEL}><em>Tenant default (all contexts)</em></MenuItem>
            {contexts.map((c) => (
              <MenuItem key={c.contextId} value={c.contextId}>
                {c.name ? `${c.contextId} — ${c.name}` : c.contextId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {contextsError && (
        <ApiErrorAlert error={contextsErrorObj} title="Couldn't load app contexts" sx={{ mb: 2 }} />
      )}
      <ApiErrorAlert error={retentionError} sx={{ mb: 2 }} />
      <ApiErrorAlert error={saveError} sx={{ mb: 2 }} />
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
          Compliance retention updated.
        </Alert>
      )}

      {retentionLoading ? (
        <CircularProgress size={22} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="cr-days-mode-label">Retention period</InputLabel>
            <Select
              labelId="cr-days-mode-label"
              label="Retention period"
              value={daysMode}
              onChange={(e) => setDaysMode(e.target.value)}
            >
              <MenuItem value={DAYS_MODE_DEFAULT}>{clearDaysLabel(contextId, retention)}</MenuItem>
              <MenuItem value={DAYS_MODE_INDEFINITE}>Indefinite</MenuItem>
              <MenuItem value={DAYS_MODE_CUSTOM}>Custom (days)</MenuItem>
            </Select>
          </FormControl>

          {daysMode === DAYS_MODE_CUSTOM && (
            <TextField
              size="small"
              type="number"
              label="Days"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              error={customDaysInvalid}
              helperText={customDaysInvalid ? 'Enter a positive whole number of days' : ' '}
              inputProps={{ min: 1, step: 1 }}
            />
          )}

          <FormControl size="small" fullWidth>
            <InputLabel id="cr-disposition-label">Disposition</InputLabel>
            <Select
              labelId="cr-disposition-label"
              label="Disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            >
              <MenuItem value="">{clearDispositionLabel(contextId, retention)}</MenuItem>
              {BASE_DISPOSITIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={saving || customDaysInvalid}
            onClick={() => save()}
            sx={{
              alignSelf: 'flex-start', bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' },
              textTransform: 'none', fontWeight: 600,
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Effective today (what the purge engine actually applies)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={`Retention: ${formatDays(retention?.effectiveDays)}`} />
              <Chip size="small" label={`Disposition: ${formatDisposition(retention?.effectiveDisposition)}`} />
            </Box>
            {contextId && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Tenant default: {formatDays(retention?.tenantDefaultDays)} · {formatDisposition(retention?.tenantDefaultDisposition)}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
